import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import menuData from "../../Data/MenuData";
import { ChevronDown } from "lucide-react";
import { usePermission } from "../../context/PermissionProvider";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();
  const { permissions, loading } = usePermission();

  const [activeMenu, setActiveMenu] = useState(null);
  const [filteredMenu, setFilteredMenu] = useState([]);

  useEffect(() => {
    const active = menuData.find((item) =>
      item.subMenu?.some((sub) => sub.path === location.pathname)
    );

    if (active?.id) {
      setActiveMenu(active.id);
    } else {
      setActiveMenu(null);
    }
  }, [location.pathname]);

  const toggleCollapse = (id) => {
    setActiveMenu((prev) => (prev === id ? null : id));
    toggleSidebar();
  };

  const isActive = (path) => location.pathname === path;

  // Keep parent active if any child is active
  const isParentActive = (item) => {
    if (item.path && isActive(item.path)) {
      return true;
    }

    if (item.subMenu) {
      return item.subMenu.some((sub) => isActive(sub.path));
    }

    return false;
  };

  // ONLY PERMISSION LOGIC
  useEffect(() => {
    if (loading) return;

    const userPages = permissions?.pages || [];

    const hasPermission = (pageName) => {
      return userPages.includes(pageName?.toLowerCase());
    };

    const newMenu = menuData.reduce((acc, item) => {
      // Parent menu with submenu
      if (item.subMenu) {
        const filteredSub = item.subMenu.filter((sub) =>
          hasPermission(sub.label)
        );

        // Show parent only if at least one child has permission
        if (filteredSub.length > 0) {
          acc.push({
            ...item,
            subMenu: filteredSub,
          });
        }
      }

      // Single menu
      else if (hasPermission(item.label)) {
        acc.push(item);
      }

      return acc;
    }, []);

    setFilteredMenu(newMenu);
  }, [permissions, loading]);

  if (loading) return null;

  return (
    <nav
      className={`sidebar sidebar-offcanvas ${isOpen ? "active" : ""}`}
      id="sidebar"
    >
      <ul className="nav">
        {filteredMenu.map((item, idx) => {
          const isSubMenu = Boolean(item.subMenu);
          const isOpenMenu = item.id === activeMenu;

          return (
            <li
              key={idx}
              className={`nav-item ${isParentActive(item) ? "active" : ""}`}
            >
              {isSubMenu ? (
                <>
                  <div
                    className={`nav-link-parent ${
                      isParentActive(item) ? "active" : ""
                    }`}
                  >
                    <Link
                      className="nav-link d-flex justify-content-between align-items-center"
                      to="#"
                      onClick={() => toggleCollapse(item.id)}
                    >
                      <div className="d-flex align-items-center gap-3">
                        {item.icon && (
                          <item.icon className="menu-icon" size={18} />
                        )}

                        <span className="custom-menu-title">
                          {item.label}
                        </span>
                      </div>

                      <ChevronDown
                        className={`menu-icon transition ${
                          isOpenMenu ? "rotate-180" : ""
                        }`}
                        size={18}
                      />
                    </Link>
                  </div>

                  <div
                    className={`collapse ${isOpenMenu ? "show" : ""}`}
                    id={item.id}
                  >
                    <ul className="nav flex-column sub-menu">
                      {item.subMenu.map((sub, subIdx) => (
                        <li
                          key={subIdx}
                          className={`nav-item ${
                            isActive(sub.path) ? "active" : ""
                          }`}
                        >
                          <Link
                            className="nav-link"
                            to={sub.path}
                            onClick={toggleSidebar}
                          >
                            <i className="fa-solid fa-arrow-right" />

                            <span className="custom-menu-subtitle mx-1">
                              {sub.label}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : (
                <div
                  className={`nav-link-parent ${
                    isParentActive(item) ? "active" : ""
                  }`}
                >
                  <Link
                    className="nav-link d-flex align-items-center"
                    to={item.path}
                    onClick={toggleSidebar}
                  >
                    <div className="d-flex align-items-center gap-3">
                      {item.icon && (
                        <item.icon className="menu-icon" size={18} />
                      )}

                      <span className="custom-menu-title">
                        {item.label}
                      </span>
                    </div>
                  </Link>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default Sidebar;